import { useState, useRef, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const IMAGES = [
  { src: "/images/00-anre-l-residence-vista-exterior.jpg", title: "Vista Exterior", caption: "Fachada principal · Bloco A · B · C", tag: "exteriores" },
  { src: "/images/09-sala-jantar-varanda.jpg", title: "Fachada Sul", caption: "Bloco B · Entrada e enquadramento urbano", tag: "exteriores" },
  { src: "/images/07-court-padel-jardins.jpg", title: "Jardim e Piscina", caption: "Zona comum · Esplanada e piscina 80m²", tag: "exteriores" },
  { src: "/images/08-fachada-bloco-b.jpg", title: "Court de Padel", caption: "Área desportiva 570m² · Jardins", tag: "exteriores" },
  { src: "/images/planta-geral-piscina.webp", title: "Planta Geral", caption: "Implantação · Piscina · Padel · Jardins", tag: "exteriores" },
  { src: "/images/vista-aerea-piscina.webp", title: "Vista Aérea", caption: "Jardins de 4 000m² e court de padel", tag: "exteriores" },
  { src: "/images/05-sala-open-space-varanda.jpg", title: "Sala de Jantar", caption: "T3 · Continuidade interior-exterior", tag: "interiores" },
  { src: "/images/01-sala-de-estar-t4.jpg", title: "Sala de Estar", caption: "T4 · Rés-do-Chão com jardim exclusivo", tag: "interiores" },
  { src: "/images/06-jardim-piscina-esplanada.jpg", title: "Cozinha e Sala", caption: "T1 · Open space com acesso à varanda", tag: "interiores" },
  { src: "/images/quarto-tipo.png", title: "Quarto Tipo", caption: "Iluminação natural · Materiais premium", tag: "interiores" },
  { src: "/images/casa-banho-tipo.png", title: "Casa de Banho Tipo", caption: "Pedra natural · Iluminação indireta", tag: "interiores" },
  { src: "/images/04-post-1.jpg", title: "Lifestyle", caption: "ANREÁL Residence · Fase de Lançamento", tag: "ambiente" },
];

const FILTERS = [
  { id: "todos", label: "Todos", count: IMAGES.length },
  { id: "exteriores", label: "Exteriores", count: IMAGES.filter((i) => i.tag === "exteriores").length },
  { id: "interiores", label: "Interiores", count: IMAGES.filter((i) => i.tag === "interiores").length },
];

export default function Gallery() {
  const [filter, setFilter] = useState("todos");
  const [lightbox, setLightbox] = useState(null); // index into filtered array
  const [progress, setProgress] = useState(0);
  const scrollerRef = useRef(null);
  const dragRef = useRef({ down: false, startX: 0, startScroll: 0, moved: 0 });

  const filtered = filter === "todos" ? IMAGES : IMAGES.filter((i) => i.tag === filter);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-gallery-card]");
    const step = card ? card.getBoundingClientRect().width + 24 : 400;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTo({ left: 0, behavior: "smooth" });
  }, [filter]);

  // Track scroll progress (0..1) for progress bar
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      setProgress(max > 0 ? el.scrollLeft / max : 0);
    };
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [filtered.length]);

  // Drag to scroll (pointer events)
  const onPointerDown = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Only react to primary button / touch / pen
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dragRef.current = {
      down: true,
      captured: false,
      pointerId: e.pointerId,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      moved: 0,
    };
    el.style.cursor = "grabbing";
  };
  const onPointerMove = (e) => {
    const st = dragRef.current;
    if (!st.down) return;
    const el = scrollerRef.current;
    const dx = e.clientX - st.startX;
    const absDx = Math.abs(dx);
    st.moved = Math.max(st.moved, absDx);
    // Only start capturing / scrolling once threshold exceeded — preserves clicks
    if (!st.captured && absDx > 6) {
      st.captured = true;
      el.style.scrollSnapType = "none";
      el.setPointerCapture?.(st.pointerId);
    }
    if (st.captured) {
      el.scrollLeft = st.startScroll - dx;
    }
  };
  const onPointerUp = () => {
    const st = dragRef.current;
    const el = scrollerRef.current;
    if (!st.down) return;
    st.down = false;
    if (el) {
      el.style.cursor = "grab";
      if (st.captured) {
        el.style.scrollSnapType = "";
        try { el.releasePointerCapture?.(st.pointerId); } catch (err) { /* ignore */ }
      }
    }
  };

  const handleCardClick = (idx) => (e) => {
    // If dragging, suppress click
    if (dragRef.current.moved > 6) {
      e.preventDefault();
      dragRef.current.moved = 0;
      return;
    }
    setLightbox(idx);
  };

  const openLightbox = useCallback((idx) => setLightbox(idx), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const nextImage = useCallback(() => setLightbox((i) => (i === null ? null : (i + 1) % filtered.length)), [filtered.length]);
  const prevImage = useCallback(() => setLightbox((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length)), [filtered.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") nextImage();
      else if (e.key === "ArrowLeft") prevImage();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, nextImage, prevImage, closeLightbox]);

  const active = lightbox !== null ? filtered[lightbox] : null;

  return (
    <section id="galeria" data-testid="gallery-section" className="py-24 lg:py-36 bg-[#F1F1EC]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10 lg:mb-14">
          <div className="lg:col-span-6 reveal">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#5C5C59] mb-6">Interiores &amp; Exteriores</div>
            <h2 className="font-display text-[36px] sm:text-[44px] lg:text-[56px] leading-[1.05] tracking-[-0.02em] text-[#1A1A1A] text-balance">
              Onde a luz entra
              <br />
              <span className="italic-display text-[#2B3B31]">e a natureza se prolonga.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 self-end reveal" style={{ transitionDelay: "120ms" }}>
            <p className="text-[15px] leading-relaxed text-[#5C5C59] font-light">
              Materiais naturais, luz abundante e continuidade entre interior e exterior. Explore a galeria completa — arraste, filtre ou clique numa imagem para ampliar.
            </p>
          </div>
        </div>

        {/* Filter + arrows toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 mb-8 lg:mb-10">
          <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="Filtro de galeria">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`gallery-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                role="tab"
                aria-selected={filter === f.id}
                className={`inline-flex items-center gap-2 h-10 px-4 text-[11px] tracking-[0.22em] uppercase transition-all duration-300 ${
                  filter === f.id ? "bg-[#2B3B31] text-[#F9F9F6]" : "bg-transparent border border-[#E5E5DF] text-[#1A1A1A] hover:border-[#2B3B31]"
                }`}
              >
                {f.label}
                <span className={`text-[10px] ${filter === f.id ? "text-[#F9F9F6]/70" : "text-[#9E9789]"}`}>{f.count}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="gallery-prev"
              onClick={() => scrollBy(-1)}
              aria-label="Anterior"
              className="w-12 h-12 lg:w-14 lg:h-14 bg-[#2B3B31] text-[#F9F9F6] flex items-center justify-center hover:-translate-y-[2px] hover:bg-[#1A2A20] transition-all duration-300"
            >
              <ChevronLeft size={20} strokeWidth={1.5} />
            </button>
            <button
              data-testid="gallery-next"
              onClick={() => scrollBy(1)}
              aria-label="Seguinte"
              className="w-12 h-12 lg:w-14 lg:h-14 bg-[#2B3B31] text-[#F9F9F6] flex items-center justify-center hover:-translate-y-[2px] hover:bg-[#1A2A20] transition-all duration-300"
            >
              <ChevronRight size={20} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Horizontal scroller */}
        <div className="relative -mx-6 lg:-mx-10">
          <div
            ref={scrollerRef}
            data-testid="gallery-scroller"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={onPointerUp}
            style={{ cursor: "grab", touchAction: "pan-y" }}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth px-6 lg:px-10 pb-4 no-scrollbar select-none"
          >
            {filtered.map((img, i) => (
              <button
                key={img.src}
                type="button"
                data-gallery-card
                data-testid={`gallery-card-${i}`}
                onClick={handleCardClick(i)}
                onDragStart={(e) => e.preventDefault()}
                className="group snap-start shrink-0 relative overflow-hidden bg-white border border-[#E5E5DF] hover:border-[#2B3B31]/60 transition-all duration-500 w-[85vw] sm:w-[540px] lg:w-[620px]"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={img.src}
                    alt={img.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute top-4 left-4 bg-[#F9F9F6]/90 backdrop-blur-sm px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase text-[#2B3B31]">
                    {img.tag === "exteriores" ? "Exterior" : img.tag === "interiores" ? "Interior" : "Ambiente"}
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                    <div className="text-left">
                      <div className="font-display text-xl lg:text-2xl text-[#F9F9F6] tracking-tight leading-tight">{img.title}</div>
                      <div className="text-[12px] text-[#F9F9F6]/85 mt-1">{img.caption}</div>
                    </div>
                    <div className="w-10 h-10 bg-[#F9F9F6] text-[#2B3B31] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                    </div>
                  </div>
                </div>
              </button>
            ))}
            <div className="shrink-0 w-1 sm:w-6" aria-hidden />
          </div>
        </div>

        {/* Scroll progress bar */}
        <div className="mt-6 h-[3px] w-full bg-[#E5E5DF] relative overflow-hidden">
          <div
            data-testid="gallery-progress"
            className="absolute inset-y-0 left-0 bg-[#2B3B31] transition-[width] duration-200"
            style={{ width: `${Math.max(8, progress * 100)}%` }}
          />
        </div>

        <div className="mt-3 flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-[#9E9789]">
          <div data-testid="gallery-count">{filtered.length} {filtered.length === 1 ? "imagem" : "imagens"}</div>
          <div className="hidden sm:flex items-center gap-2 text-[#2B3B31]">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11V6.5a1.5 1.5 0 0 1 3 0V11"/><path d="M12 11V5.5a1.5 1.5 0 0 1 3 0V11"/><path d="M15 11V7.5a1.5 1.5 0 0 1 3 0v6a7 7 0 0 1-14 0V9a1.5 1.5 0 0 1 3 0v2"/></svg>
            <span>Arraste com o cursor · use as setas · clique para ampliar</span>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && closeLightbox()}>
        <DialogContent
          data-testid="gallery-lightbox"
          className="max-w-[95vw] w-[95vw] sm:max-w-[92vw] h-[92vh] p-0 gap-0 border-0 bg-[#0d100e] text-[#F9F9F6] rounded-none overflow-hidden [&>button]:hidden"
        >
          {active && (
            <>
              <DialogTitle className="sr-only">{active.title}</DialogTitle>
              <DialogDescription className="sr-only">{active.caption}</DialogDescription>
              <div className="relative w-full h-full flex flex-col">
                {/* Top bar */}
                <div className="flex items-center justify-between px-5 sm:px-8 py-4 border-b border-white/10">
                  <div>
                    <div className="text-[10px] tracking-[0.24em] uppercase text-white/60 mb-1">
                      {lightbox + 1} / {filtered.length} · {active.tag === "exteriores" ? "Exterior" : active.tag === "interiores" ? "Interior" : "Ambiente"}
                    </div>
                    <div className="font-display text-lg sm:text-xl tracking-tight">{active.title}</div>
                  </div>
                  <button
                    data-testid="lightbox-close"
                    onClick={closeLightbox}
                    aria-label="Fechar"
                    className="w-11 h-11 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <X size={22} strokeWidth={1.25} />
                  </button>
                </div>

                {/* Image */}
                <div className="relative flex-1 min-h-0 flex items-center justify-center px-3 sm:px-16">
                  <img
                    src={active.src}
                    alt={active.title}
                    className="max-w-full max-h-full object-contain"
                  />
                  <button
                    data-testid="lightbox-prev"
                    onClick={prevImage}
                    aria-label="Imagem anterior"
                    className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center bg-white/5 hover:bg-white/15 text-white transition-colors"
                  >
                    <ChevronLeft size={22} strokeWidth={1.25} />
                  </button>
                  <button
                    data-testid="lightbox-next"
                    onClick={nextImage}
                    aria-label="Imagem seguinte"
                    className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center bg-white/5 hover:bg-white/15 text-white transition-colors"
                  >
                    <ChevronRight size={22} strokeWidth={1.25} />
                  </button>
                </div>

                {/* Bottom bar */}
                <div className="flex items-center justify-between px-5 sm:px-8 py-4 border-t border-white/10">
                  <div className="text-[13px] text-white/70 max-w-[70%]">{active.caption}</div>
                  <div className="flex sm:hidden gap-2">
                    <button onClick={prevImage} className="w-10 h-10 flex items-center justify-center bg-white/5 text-white" aria-label="Anterior">
                      <ChevronLeft size={18} strokeWidth={1.25} />
                    </button>
                    <button onClick={nextImage} className="w-10 h-10 flex items-center justify-center bg-white/5 text-white" aria-label="Seguinte">
                      <ChevronRight size={18} strokeWidth={1.25} />
                    </button>
                  </div>
                  <div className="hidden sm:block text-[10px] tracking-[0.22em] uppercase text-white/40">← / → · Esc</div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
